import * as React from 'react'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { Popup, PopupFormWrapper } from 'traveldesk-ui'
import OffersApiService from '../../services/OffersApiService'
import {
    OfferHistoryItem, OfferFieldChange, parseOfferChanges,
    historySourceLabel, historyActionLabel, historyFieldLabel, historyValueLabel,
} from '../../models/OfferHistory'
import './OfferHistory.css'

interface Props {
    offerId: number
    offerName?: string
    onClose: () => void
}

const formatDate = (date: string, withTime = false) => {
    const parsed = moment(date)
    return parsed.isValid() ? parsed.format(withTime ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY') : date
}

const ChangeList = ({ changes }: { changes: OfferFieldChange[] }) => (
    <dl className='offer-history-changes'>
        {changes.map((change, index) => (
            <div className='offer-history-change' key={`${change.fieldName}-${index}`}>
                <dt>{historyFieldLabel(change.fieldName)}</dt>
                <dd>
                    <span className='offer-history-before' aria-label='Before'>{historyValueLabel(change.before)}</span>
                    <span className='offer-history-arrow' aria-label='changed to'> → </span>
                    <span className='offer-history-after' aria-label='After'>{historyValueLabel(change.after)}</span>
                </dd>
            </div>
        ))}
    </dl>
)

const PayloadViewer = ({ offerId, historyId }: { offerId: number; historyId: number }) => {
    const dispatch = useDispatch()
    const [payload, setPayload] = React.useState<string>()
    const [error, setError] = React.useState<string>()
    const [attempt, setAttempt] = React.useState(0)
    React.useEffect(() => {
        let active = true
        setPayload(undefined)
        setError(undefined)
        const load = async () => {
            try {
                const response = await OffersApiService.getHistoryPayload(dispatch, offerId, historyId)
                if (!active) return
                if (!response.IsOk || typeof response.Content?.payload !== 'string') {
                    setError('The full payload could not be loaded. It may be unavailable or storage may be temporarily inaccessible.')
                    return
                }
                const json = response.Content.payload
                try {
                    setPayload(JSON.stringify(JSON.parse(json), null, 2))
                } catch {
                    setPayload(json)
                }
            } catch {
                if (active) setError('The full payload could not be loaded. Please try again.')
            }
        }
        load()
        return () => { active = false }
    }, [dispatch, offerId, historyId, attempt])

    return (
        <section className='offer-history-payload' aria-label='Full payload'>
            {error ? (
                <div role='alert'>
                    <p>{error}</p>
                    <button type='button' className='offer-history-link' onClick={() => setAttempt(attempt + 1)}>Retry payload</button>
                </div>
            ) : payload === undefined ? <p role='status'>Loading payload…</p> : (
                <pre tabIndex={0}>{payload}</pre>
            )}
        </section>
    )
}

const HistoryRow = ({ item, offerId }: { item: OfferHistoryItem; offerId: number }) => {
    const [showPayload, setShowPayload] = React.useState(false)
    const details = parseOfferChanges(item.changesDetails)
    const isCreated = item.action === 0
    const periods = item.validPeriodsHistory || []
    return (
        <li className='offer-history-item'>
            <article>
                <header className='offer-history-row-header'>
                    <h3>
                        <span>{item.userName || item.userId || 'Unknown user'}</span>
                        <span className='offer-history-divider'> · </span>
                        <time dateTime={item.dateTime}>{formatDate(item.dateTime, true)}</time>
                        <span className='offer-history-divider'> · </span>
                        <span className='offer-history-source'>{historySourceLabel(item.source)}</span>
                    </h3>
                    <span className='offer-history-action'>{historyActionLabel(item.action)}</span>
                </header>
                {!isCreated && (
                    <div className='offer-history-details'>
                        {details.error && <p role='alert'>{details.error}</p>}
                        <ChangeList changes={details.changes} />
                        {periods.map(period => {
                            const periodDetails = parseOfferChanges(period.changesDetails)
                            return (
                                <section className='offer-history-period' key={period.id}>
                                    <h4>
                                        Valid period #{period.offerValidPeriodId}: {formatDate(period.periodStart)}
                                        {' – '}{period.periodEnd ? formatDate(period.periodEnd) : 'No end date'}
                                        {' · '}{['Added', 'Changed', 'Deleted'][period.operation] || 'Changed'}
                                    </h4>
                                    {periodDetails.error ? <p role='alert'>{periodDetails.error}</p> : <ChangeList changes={periodDetails.changes} />}
                                </section>
                            )
                        })}
                        {details.secondaryPeriods.map(period => (
                            <section className='offer-history-period' key={`secondary-${period.id}`}>
                                <h4>
                                    Secondary supplier {period.supplierName ? `${period.supplierName} (#${period.supplierId})` : `#${period.supplierId}`}
                                    {' · '}Pricing period #{period.id}: {formatDate(period.periodStart)} – {formatDate(period.periodEnd)}
                                    {' · '}{['Added', 'Changed', 'Deleted'][period.operation] || 'Changed'}
                                </h4>
                                <ChangeList changes={period.changes} />
                            </section>
                        ))}
                        {!details.error && details.changes.length === 0 && periods.length === 0 && details.secondaryPeriods.length === 0 && (
                            <p className='offer-history-note'>
                                {details.isBaseline ? 'Baseline snapshot — no earlier comparable history.' :
                                    item.changesDetails ? 'No field changes recorded.' :
                                        'Change details are not available for this legacy entry. View the full payload.'}
                            </p>
                        )}
                        {details.warnings.length > 0 && (
                            <details className='offer-history-note'>
                                <summary>Legacy reconstruction notes</summary>
                                <ul>{details.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
                            </details>
                        )}
                    </div>
                )}
                <button
                    type='button'
                    className='offer-history-link'
                    disabled={!item.hasPayload}
                    aria-expanded={showPayload}
                    aria-controls={`offer-history-payload-${item.id}`}
                    onClick={() => setShowPayload(!showPayload)}>
                    {!item.hasPayload ? 'Payload unavailable' : showPayload ? 'Hide full payload' : 'View full payload'}
                </button>
                {showPayload && <div id={`offer-history-payload-${item.id}`}><PayloadViewer offerId={offerId} historyId={item.id} /></div>}
            </article>
        </li>
    )
}

const OfferHistory = ({ offerId, offerName, onClose }: Props) => {
    const dispatch = useDispatch()
    const [history, setHistory] = React.useState<OfferHistoryItem[]>([])
    const [isLoading, setIsLoading] = React.useState(true)
    const [error, setError] = React.useState<string>()
    const [attempt, setAttempt] = React.useState(0)
    const dialog = React.useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        const previousFocus = document.activeElement as HTMLElement | null
        dialog.current?.focus()
        return () => { previousFocus?.focus() }
    }, [])
    React.useEffect(() => {
        let active = true
        setIsLoading(true)
        setError(undefined)
        setHistory([])
        const load = async () => {
            try {
                const response = await OffersApiService.getHistory(dispatch, offerId)
                if (!active) return
                if (response.IsOk && Array.isArray(response.Content)) {
                    setHistory(response.Content.slice().sort((a, b) =>
                        (moment(b.dateTime).valueOf() - moment(a.dateTime).valueOf()) || b.id - a.id))
                } else {
                    setError('Offer history could not be loaded. Please try again.')
                }
            } catch {
                if (active) setError('Offer history could not be loaded. Please try again.')
            } finally {
                if (active) setIsLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [dispatch, offerId, attempt])

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            onClose()
        }
        if (event.key === 'Tab' && dialog.current) {
            const elements = dialog.current.querySelectorAll<HTMLElement>('button:not([disabled]), summary, [tabindex="0"]')
            const first = elements[0]
            const last = elements[elements.length - 1]
            if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
                event.preventDefault()
                last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first?.focus()
            }
        }
    }

    return (
        <Popup onClose={onClose}>
            <PopupFormWrapper className='offer-history'>
                <div ref={dialog} role='dialog' aria-modal='true' aria-labelledby='offer-history-title' tabIndex={-1} onKeyDown={onKeyDown}>
                    <header className='offer-history-header'>
                        <div>
                            <h2 id='offer-history-title'>Offer history</h2>
                            <p>{offerName || `Offer #${offerId}`} · Newest first</p>
                        </div>
                        <div className='offer-history-actions'>
                            <button type='button' className='offer-history-link' disabled={isLoading} onClick={() => setAttempt(attempt + 1)}>Refresh</button>                            
                        </div>
                    </header>
                    {isLoading ? <p role='status'>Loading history…</p> : error ? (
                        <div role='alert'>
                            <p>{error}</p>
                            <button type='button' className='offer-history-link' onClick={() => setAttempt(attempt + 1)}>Retry</button>
                        </div>
                    ) : history.length === 0 ? <p>No history has been recorded for this offer.</p> : (
                        <ol className='offer-history-timeline'>
                            {history.map(item => <HistoryRow key={`${offerId}-${item.id}`} item={item} offerId={offerId} />)}
                        </ol>
                    )}
                </div>
            </PopupFormWrapper>
        </Popup>
    )
}

export default OfferHistory
